import { useEffect, useRef, useState } from 'react'

import {
  type Anchor,
  type BoardElement,
  type DisplayMode,
  contentIsGone,
  titleOf,
} from '../../api/boards'
import { canBeWrittenIn, contentUrl, previewUrl } from '../../api/library'
import { timeLeft } from '../../api/tasks'
import { Button } from '../../ui/Control'
import { Preview } from '../../ui/preview/Preview'
import { useSaving } from '../../ui/useSaving'
import { ANCHORS } from './routing'
import { PdfPresentation } from './PdfPresentation'
import { WrittenDocument } from './WrittenDocument'
import { MARKDOWN } from '../../ui/markup'
import { RichText } from '../../ui/RichText'

/**
 * One thing lying on a board.
 *
 * The rule the library follows applies here unchanged: **content is shown, not named.** A card on a
 * document is its first page, a card on a note is its first line drawn as it was written, a card on
 * an open point is the sentence with the time left beside it. A grey rectangle with a file name on
 * it would be the file list this product is meant not to feel like — and on a board, where the whole
 * point is seeing how things relate, it would be worse than in a list.
 *
 * Three things this component decides:
 *
 * **A picture is fetched when the card comes into view, and not before.** A board with a hundred
 * cards would otherwise ask the service for a hundred renderings the moment it opens, and wait for
 * none of them usefully. Until it arrives, the card shows its title — which is what a card whose
 * document cannot be drawn shows for ever anyway.
 *
 * **A card whose content has gone keeps its place.** It says what lay there, from the caption copied
 * when it was put down. An arrangement with a silent hole in it has stopped being readable, and that
 * is a property of every board rather than of a copy.
 *
 * **Nothing here is reachable by gesture alone.** Dragging and the corner handles are the quick way;
 * every one of them is also a control in the toolbar, which is what makes the board usable with a
 * keyboard and explicable in a sentence.
 */
export interface ElementProps {
  readonly element: BoardElement
  readonly selected: boolean
  /** Held highlighted after a jump — from a search hit, or from finding something on the board. */
  readonly marked: boolean
  /** `false` on a phone: a board is looked at and walked over there, not rearranged. */
  readonly rearrangeable: boolean
  /** Whether a line is being drawn right now, so a press on this element begins one. */
  readonly connecting: boolean
  /**
   * Whether an end of a line is being aimed — while it is being drawn, *or* while an end that
   * already exists is being dragged somewhere else. Both are aiming, and both need something to aim
   * at, which is what the anchor points are.
   */
  readonly aiming: boolean
  /**
   * Where an end being aimed would land on *this* element right now, or `null` when it would not.
   *
   * `auto` means "on this one, and the line works out where" — which is what the middle of a card
   * gives. Shown so that the rule is visible while it is being used rather than only afterwards.
   */
  readonly aimedAt: Anchor | null
  readonly onSelect: (element: BoardElement, adding: boolean) => void
  /** Begins a line here, at the anchor that was taken hold of — or wherever it fastens itself. */
  readonly onStartLine: (element: BoardElement, anchor: Anchor, event: React.PointerEvent) => void
  /** Opens what a card points at, in the area that owns it. */
  readonly onOpen: (element: BoardElement) => void
  readonly onDragStart: (element: BoardElement, event: React.PointerEvent) => void
  readonly onResizeStart: (element: BoardElement, corner: Corner, event: React.PointerEvent) => void
  /** What was typed into a sticky note or a text block, saved a moment after the typing stops. */
  readonly onWrite: (element: BoardElement, text: string) => Promise<void>
  /** Which markup this element's text is written in. A label; no byte of the text moves. */
  readonly onMarkup: (element: BoardElement, format: string) => Promise<void>
  /** Remembers the page reached in a PDF presentation without adding it to board undo. */
  readonly onDisplayPage: (element: BoardElement, page: number) => void
  /**
   * Switches one card between reading and writing a document, from the card itself.
   *
   * The panel beside the board says the same thing — this is the shortcut, as everywhere here. Like
   * turning a page it is reading state and not an authoring change, so it stays out of board undo.
   */
  readonly onDisplayMode: (element: BoardElement, mode: DisplayMode) => void
  /**
   * Whether this element is open for writing.
   *
   * **Held by the board and not in here.** The mouse opens it through the element's `dblclick`; touch
   * opens it through a pair of taps recognised by the board, which is the only thing that knows
   * whether either press became a drag.
   */
  readonly writing: boolean
  readonly onWriting: (writing: boolean) => void
}

export type Corner = 'nw' | 'ne' | 'se' | 'sw'

const CORNERS: readonly Corner[] = ['nw', 'ne', 'se', 'sw']

export function Element({
  element,
  selected,
  marked,
  rearrangeable,
  connecting,
  aiming,
  aimedAt,
  onSelect,
  onStartLine,
  onOpen,
  onDragStart,
  onResizeStart,
  onWrite,
  onMarkup,
  onDisplayPage,
  onDisplayMode,
  writing,
  onWriting,
}: ElementProps) {
  const classes = [
    'thing',
    `thing--${element.kind}`,
    selected ? 'thing--selected' : null,
    marked ? 'thing--marked' : null,
    element.highlighted ? 'thing--highlighted' : null,
    element.pinned ? 'thing--pinned' : null,
    element.locked ? 'thing--locked' : null,
    connecting || aiming ? 'thing--connectable' : null,
    aimedAt !== null ? 'thing--aimed' : null,
  ]
    .filter((one) => one !== null)
    .join(' ')

  return (
    <div
      className={classes}
      data-element={element.id}
      data-colour={element.colour ?? undefined}
      style={{
        left: `${element.x}px`,
        top: `${element.y}px`,
        width: `${element.width}px`,
        height: `${element.height}px`,
      }}
      /*
        `touch-action: none` on everything that is dragged, in the stylesheet rather than here. Without
        it Safari on iOS takes the first move of a drag as a scroll and the element never receives the
        rest of it — which is the difference between a board that can be rearranged on an iPad and one
        that cannot.
      */
      onPointerDown={(event) => {
        // The primary button and nothing else; a secondary press is the browser's business.
        if (event.button !== 0) {
          return
        }
        if (connecting) {
          // A press anywhere on the element begins a line that fastens itself — the shortest way to
          // wherever it ends up. One of the anchor dots below begins one that stays exactly there.
          // Deliberately *not* a selection: while a line is being drawn, an element is an end of it.
          onStartLine(element, 'auto', event)
          return
        }
        onSelect(element, event.shiftKey || event.metaKey || event.ctrlKey)
        // A locked element is not dragged. Everything else about it still answers.
        if (rearrangeable && !element.locked) {
          onDragStart(element, event)
        }
      }}
      /*
       * A double click opens the words, and it is the plainest of the three ways in — the one a Mac
       * or a PC reaches for without being told. It works again because the pointer capture is no
       * longer taken on the press: a press that never moved leaves both clicks on this element, so
       * the pair forms. A press that *did* move never gets here, which is right — that was a drag.
       *
       * The board decides the touch equivalent from the gesture itself, because a finger produces no
       * reliable `dblclick` on a surface like this. Neither way is the only one, and the right-click
       * menu says it in words for whoever finds neither.
       */
      onDoubleClick={(event) => {
        if (connecting || !rearrangeable || element.locked) {
          return
        }
        if (element.kind === 'sticky' || element.kind === 'text') {
          event.stopPropagation()
          onWriting(true)
        }
      }}
    >
      <Body
        element={element}
        connecting={connecting}
        onOpen={onOpen}
        onWrite={onWrite}
        onMarkup={onMarkup}
        onDisplayPage={onDisplayPage}
        onDisplayMode={onDisplayMode}
        editable={rearrangeable && !element.locked}
        writing={writing}
        onWriting={onWriting}
      />

      {/*
        Where a line can be fastened, shown while one is being drawn — the four sides and the four
        corners. Pressing one begins a line that stays exactly there; pressing anywhere else on the
        element begins one that finds its own way to the edge. They are only ever visible while
        connecting: eight dots on every card the rest of the time would be a board covered in dots.
      */}
      {(connecting || aiming) &&
        ANCHORS.map((anchor) => (
          <span
            key={anchor}
            className={
              aimedAt === anchor
                ? `thing__anchor thing__anchor--${anchor} thing__anchor--aimed`
                : `thing__anchor thing__anchor--${anchor}`
            }
            title={`Fasten a line at the ${anchor.replace('-', ' ')}`}
            aria-hidden="true"
            onPointerDown={(event) => {
              event.stopPropagation()
              if (event.button === 0) {
                onStartLine(element, anchor, event)
              }
            }}
          />
        ))}

      {/*
        The corner handles, and only on a selection of one. On several they would be four handles
        per element with no agreed meaning between them, and resizing a group is not something this
        board offers — the toolbar's own controls are what act on a whole selection.
      */}
      {rearrangeable && selected && !element.locked && (
        <>
          {CORNERS.map((corner) => (
            <span
              key={corner}
              className={`thing__grip thing__grip--${corner}`}
              aria-hidden="true"
              onPointerDown={(event) => {
                event.stopPropagation()
                onResizeStart(element, corner, event)
              }}
            />
          ))}
        </>
      )}
    </div>
  )
}

function Body({
  element,
  connecting,
  onOpen,
  onWrite,
  onMarkup,
  onDisplayPage,
  onDisplayMode,
  editable,
  writing,
  onWriting,
}: {
  readonly element: BoardElement
  readonly connecting: boolean
  readonly onOpen: (element: BoardElement) => void
  readonly onWrite: (element: BoardElement, text: string) => Promise<void>
  /** Which markup this element's text is written in. A label; no byte of the text moves. */
  readonly onMarkup: (element: BoardElement, format: string) => Promise<void>
  readonly onDisplayPage: (element: BoardElement, page: number) => void
  readonly onDisplayMode: (element: BoardElement, mode: DisplayMode) => void
  readonly editable: boolean
  readonly writing: boolean
  readonly onWriting: (writing: boolean) => void
}) {
  if (element.kind === 'frame') {
    return <Frame element={element} />
  }
  if (element.kind === 'sticky' || element.kind === 'text') {
    return (
      <Written
        element={element}
        onWrite={onWrite}
        onMarkup={onMarkup}
        editable={editable && !connecting}
        writing={writing}
        onWriting={onWriting}
      />
    )
  }
  return (
    <Card
      element={element}
      onOpen={onOpen}
      onDisplayPage={onDisplayPage}
      onDisplayMode={onDisplayMode}
      editable={editable}
      connecting={connecting}
    />
  )
}

/**
 * A frame: a rectangle with a title, and nothing inside it of its own.
 *
 * What it carries is decided by geometry every time it moves — see `Canvas` — rather than written
 * down, so putting something into a frame is dropping it inside the rectangle and taking it out is
 * dragging it clear. There is nothing to join and nothing to leave.
 */
function Frame({ element }: { readonly element: BoardElement }) {
  return (
    <div className="thing__frame">
      <span className="thing__frameName">
        {element.label === '' ? 'Frame' : (element.label ?? 'Frame')}
      </span>
    </div>
  )
}

/** A sticky note or a text block: written here, edited here, and existing nowhere else. */
function Written({
  element,
  onWrite,
  onMarkup,
  editable,
  writing,
  onWriting,
}: {
  readonly element: BoardElement
  readonly onWrite: (element: BoardElement, text: string) => Promise<void>
  /** Which markup this element's text is written in. A label; no byte of the text moves. */
  readonly onMarkup: (element: BoardElement, format: string) => Promise<void>
  readonly editable: boolean
  readonly writing: boolean
  readonly onWriting: (writing: boolean) => void
}) {
  const [text, setText] = useState(element.text ?? '')
  const [known, setKnown] = useState(element.text ?? '')
  const writingBox = useRef<HTMLDivElement>(null)

  // What arrived from somewhere else — another tab, another device — while this is not being typed
  // in. Adjusted during the render rather than in an effect: it is a value derived from a property,
  // and an effect would draw the note once with the old text and once with the new.
  if (!writing && (element.text ?? '') !== known) {
    setKnown(element.text ?? '')
    setText(element.text ?? '')
  }

  const saving = useSaving(async (wanted) => {
    if (wanted.trim() === '') {
      // A sticky note that says nothing is not one, and the service refuses it. Nothing is sent; the
      // text stays on the screen until somebody types something or takes the note off the board.
      return
    }
    await onWrite(element, wanted)
  })

  /*
   * Finish from an explicit interaction *outside* the editor, not from `blur` alone. CodeMirror
   * moves focus while it mounts, and the browser may also finish the double click after the editor
   * has replaced its button. Both produce a blur with no meaningful destination; treating either as
   * "done" made the editor flash open and immediately close again.
   *
   * Kept in a ref so the document listeners stay attached for one editing session while still using
   * the current save and parent callbacks after a render.
   */
  const finishWriting = useRef(() => {})
  useEffect(() => {
    finishWriting.current = () => {
      saving.now()
      onWriting(false)
    }
  })

  useEffect(() => {
    if (!writing) {
      return
    }
    const outside = (event: Event) => {
      const target = event.target
      if (target instanceof Node && !writingBox.current?.contains(target)) {
        finishWriting.current()
      }
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('focusin', outside)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('focusin', outside)
    }
  }, [writing])

  if (writing) {
    return (
      <div
        ref={writingBox}
        className="thing__writing"
        onPointerDown={(event) => {
          // Selecting text and placing the cursor belong to the editor, never to the board's drag.
          event.stopPropagation()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            finishWriting.current()
          }
        }}
      >
        <RichText
          compact
          text={text}
          format={element.format ?? MARKDOWN}
          label={element.kind === 'sticky' ? 'Sticky note' : 'Text block'}
          purpose="board-element"
          initially="text"
          autoFocus
          placeholder="Write it down."
          onChange={(wanted) => {
            setText(wanted)
            saving.typed(wanted)
          }}
          onFormat={async (wanted) => {
            await onMarkup(element, wanted)
          }}
        />
        <Button
          tone="bare"
          className="thing__done"
          onClick={() => {
            finishWriting.current()
          }}
        >
          Done
        </Button>
      </div>
    )
  }

  return (
    <button
      type="button"
      className="thing__written"
      // The words are what somebody points at, so the control that opens them for writing says what
      // it is rather than leaving a screen reader with the text and no verb.
      aria-label={`Write on this ${element.kind === 'sticky' ? 'sticky note' : 'text block'}`}
      disabled={!editable}
      /*
       * The keyboard's way in, and only that. Mouse double clicks bubble to the element; the board
       * recognises touch taps — see `writing` in the props above.
       */
      onClick={(event) => {
        if (editable && event.detail === 0) {
          onWriting(true)
        }
      }}
    >
      {text.trim() === '' ? (
        <span className="thing__empty">Write something</span>
      ) : (
        <Preview text={text} format={element.format ?? MARKDOWN} label="What this says" />
      )}
    </button>
  )
}

/**
 * A card on something that lives somewhere else.
 *
 * It shows the content and never a name for it, and it opens what it points at in the area that owns
 * it — a document in the library, a note on the pad, an open point in the list. A card is a *view*
 * of that content: taking it off the board leaves it entirely alone.
 */
function Card({
  element,
  onOpen,
  onDisplayPage,
  onDisplayMode,
  editable,
  connecting,
}: {
  readonly element: BoardElement
  readonly onOpen: (element: BoardElement) => void
  readonly onDisplayPage: (element: BoardElement, page: number) => void
  readonly onDisplayMode: (element: BoardElement, mode: DisplayMode) => void
  /** Whether this card may be written in at all: not on a phone, and not while it is locked. */
  readonly editable: boolean
  /** While a line is being drawn, a card is one of its ends and never a way out of the board. */
  readonly connecting: boolean
}) {
  const gone = contentIsGone(element)
  const interactive = element.document !== null && liveFormOf(element) !== null

  return (
    <div
      role={interactive ? undefined : 'button'}
      tabIndex={gone || interactive ? -1 : 0}
      className={gone ? 'thing__card thing__card--gone' : 'thing__card'}
      /*
       * **Opening is a double click, never a single one.** On a board a click is how something is
       * chosen, and a card that navigated away on the first one meant every attempt to select, drag
       * or connect it left the desk instead. The visible way is the panel beside the board, which
       * carries "Open in the library" whenever a single card is selected — so the gesture is a
       * shortcut for a control, exactly as everywhere else here.
       */
      onDoubleClick={() => {
        if (!gone && !connecting) {
          onOpen(element)
        }
      }}
      onKeyDown={(event) => {
        if (!gone && !interactive && !connecting && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          onOpen(element)
        }
      }}
    >
      <div className="thing__picture">
        {gone ? (
          <span className="thing__instead" aria-hidden="true">
            GONE
          </span>
        ) : element.document !== null ? (
          <DocumentPicture
            element={element}
            onDisplayPage={onDisplayPage}
            onDisplayMode={onDisplayMode}
            editable={editable}
          />
        ) : element.note !== null ? (
          <span className="thing__note">
            <Preview text={element.note.text} format={element.note.format} label="First lines" />
          </span>
        ) : (
          <span className="thing__instead" aria-hidden="true">
            TASK
          </span>
        )}
      </div>
      <div className="thing__caption">
        <span className="thing__title">{titleOf(element)}</span>
        <span className="thing__facts">
          {gone ? (
            /* Said out loud, at the place it lay. The arrangement stays readable; the content is
               what has gone, and nothing about the board pretends otherwise. */
            <span className="thing__missing">No longer in MyLib</span>
          ) : element.document !== null ? (
            <span>{element.document.kind === '' ? 'File' : element.document.kind}</span>
          ) : element.task !== null ? (
            <span>
              {element.task.doneAt !== null
                ? 'Ticked off'
                : element.task.dueOn !== null
                  ? timeLeft(element.task)
                  : 'Task'}
            </span>
          ) : (
            <span>Note</span>
          )}
        </span>
      </div>
    </div>
  )
}

/**
 * The picture of a document's content, fetched **once the card is in view**.
 *
 * The one thing a board adds to how previews work: the service still renders on first request and
 * keeps the result under the content's checksum, so nothing about the storage changes. What changes
 * is when the browser asks. A board of a hundred cards then starts as many renderings as somebody is
 * actually looking at, and opening it waits for none of them.
 */
function DocumentPicture({
  element,
  onDisplayPage,
  onDisplayMode,
  editable,
}: {
  readonly element: BoardElement
  readonly onDisplayPage: (element: BoardElement, page: number) => void
  readonly onDisplayMode: (element: BoardElement, mode: DisplayMode) => void
  readonly editable: boolean
}) {
  const document = element.document
  const live = liveFormOf(element)

  if (document !== null && live === 'pdf-document') {
    return <iframe className="thing__document" src={contentUrl(document)} title={document.title} />
  }
  if (document !== null && live === 'pdf-presentation') {
    return (
      <PdfPresentation
        document={document}
        page={element.displayPage}
        onPage={(page) => {
          onDisplayPage(element, page)
        }}
      />
    )
  }
  if (document !== null && live === 'text') {
    return (
      <WrittenDocument
        document={document}
        writing={element.displayMode === 'writing'}
        editable={editable}
        onMode={(mode) => {
          onDisplayMode(element, mode)
        }}
      />
    )
  }

  return <StaticPicture element={element} />
}

/**
 * Which live form this card is in, or `null` for the ordinary first-page picture.
 *
 * **The document decides which modes it has, and this is the one place that says so.** A PDF is
 * finished, so its live forms are ways of *looking* at it — the browser's viewer, or one page at a
 * time. A marked-up text is on a board because somebody is working on it, so its live forms are the
 * text as it is set and the text being written; embedding that in the browser's viewer would show
 * the markup as source, which is the file and not the document. A mode that means nothing for this
 * document falls back to the picture rather than to an empty card.
 */
function liveFormOf(element: BoardElement): 'pdf-document' | 'pdf-presentation' | 'text' | null {
  const document = element.document
  if (document === null || element.displayMode === null) {
    return null
  }
  if (document.mediaType === 'application/pdf') {
    return element.displayMode === 'document'
      ? 'pdf-document'
      : element.displayMode === 'presentation'
        ? 'pdf-presentation'
        : null
  }
  if (canBeWrittenIn(document)) {
    return element.displayMode === 'document' || element.displayMode === 'writing' ? 'text' : null
  }
  return null
}

function StaticPicture({ element }: { readonly element: BoardElement }) {
  const document = element.document
  const host = useRef<HTMLSpanElement>(null)
  // A browser without an observer — or a test — asks straight away. Fewer requests is a saving;
  // never asking would be a card that never shows its content.
  const [inView, setInView] = useState(() => typeof IntersectionObserver !== 'function')
  const [drawn, setDrawn] = useState(document?.hasPreview ?? false)

  useEffect(() => {
    const node = host.current
    if (node === null || inView) {
      return
    }
    const watching = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true)
        }
      },
      // A margin, so a card is fetched just before it is scrolled or panned into view rather than
      // at the moment its edge appears.
      { rootMargin: '200px' },
    )
    watching.observe(node)
    return () => {
      watching.disconnect()
    }
  }, [inView])

  return (
    <span className="thing__frameless" ref={host}>
      {document !== null && drawn && inView ? (
        <img
          className="thing__image"
          // The whole first page by default, rather than as much of it as covers the tile: a page
          // cropped to a square shows its top third and hides where the words go. A photograph reads
          // better edge to edge, so the card says which it wants.
          data-fit={element.fit ?? 'fit'}
          src={previewUrl(document)}
          alt=""
          decoding="async"
          draggable={false}
          onError={() => {
            setDrawn(false)
          }}
        />
      ) : (
        <span className="thing__instead" aria-hidden="true">
          {document === null || document.kind === '' ? 'FILE' : document.kind}
        </span>
      )}
    </span>
  )
}
